import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Stack,
  Link,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ServiceUpload from "./admin/components/ServiceUpload";
import { useDispatch, useSelector } from "react-redux";
import { serviceRequestsActions } from "../state/serviceRequests/actions";
import { serviceRequestsSelectors, uiSelectors } from "../state";
import Sidebar from "../layouts/sidebar/Sidebar";
import { REACT_APP_ASSETS_URL } from "../config";
import { useMobile } from "../themes";

export default function ServiceRequestUpdateForm() {
  const serviceRequestStatus = useSelector(
    serviceRequestsSelectors.serviceRequestDataStatus,
  );
  const { uuid } = useParams<{ uuid: string }>();
  const [files, setFiles] = useState(new FormData());
  const [message, setMessage] = useState("");
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const { logo_height, logo_path } = useSelector(uiSelectors.agencyData);

  // Fetch status when component loads
  useEffect(() => {
    if (uuid) {
      dispatch(serviceRequestsActions.getServiceRequestStatus(uuid));
    }
  }, [dispatch, uuid]);

  // send message and files to backend
  const handleSubmit = () => {
    if (!message.trim() && (!files || !files.get("file"))) {
      return; // Don't submit if both message and files are empty
    }
    dispatch(
      serviceRequestsActions.clientUpdateServiceRequest(uuid, message, files),
    );
    setMessage("");
    setFiles(new FormData());
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: isMobile ? "column" : "row",
        overflow: "hidden",
      }}
    >
      {isMobile && (
        <Box
          sx={{
            padding: "32px 30px",
            margin: "auto",
            "& img": {
              maxWidth: 255,
              height: logo_height || "80px",
            },
          }}
        >
          {logo_path && (
            <Link href="/">
              <img src={`${REACT_APP_ASSETS_URL}/${logo_path}`} alt="logo" />
            </Link>
          )}
        </Box>
      )}
      {!isMobile && (
        <Sidebar mobileOpen={undefined} setMobileOpen={undefined} />
      )}
      <Box
        sx={{
          width: "100%",
          mt: isMobile ? 0 : "40px",
        }}
      >
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <Box component="form" noValidate>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                Update Service Request
              </Typography>

              {/* Display service request status */}
              <Typography
                variant="h6"
                align="center"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color:
                    serviceRequestStatus === "Completed"
                      ? "success.main"
                      : serviceRequestStatus === "In Progress"
                      ? "info.main"
                      : serviceRequestStatus === "Pending Response"
                      ? "warning.main"
                      : "text.primary",
                }}
              >
                Status: {serviceRequestStatus || "Loading..."}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Please provide any additional information or documents related
                to your service request.
              </Typography>

              <Stack spacing={4}>
                <TextField
                  label="Message"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  placeholder="Type your message here..."
                  sx={{ mb: 2 }}
                />

                <Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      bgcolor: "background.default",
                    }}
                  >
                    <ServiceUpload setFiles={setFiles} />
                  </Paper>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!message.trim() && (!files || !files.get("file"))}
                    size="large"
                    sx={{
                      minWidth: 120,
                      py: 1,
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
